// GST and compliance (docs/cockpit/spec.md, under Money and compliance; #486): for a period, its GST position (tax on
// sales, input tax credit and the net to pay in cash) as the GSTR-3B draft has it; the GSTR-1 and GSTR-3B exports and
// the TDS return data prepared for the CA, each version's files downloadable; and the filings that fall due in the month
// after the period, with their dates, those past them late. It reads only `GET /api/gst` and changes nothing:
// preparing a return or recording a filing is done in Chat. A period with nothing taxable says what to record.
import { useEffect, useState, type ReactNode } from "react";
import { rupees } from "../api.js";
import { ApiError } from "../http.js";
import { formatDate } from "../i18n/format.js";
import { useLanguage, useWords } from "../i18n/words.js";
import { dayOf } from "../lists/List.js";
import { exportHref, type CalendarEntry, type ExportView, type GstApi, type GstPage, type Heads } from "./api.js";

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id}>
      <h3 className="sectionh" id={id}>
        {title}
      </h3>
      {children}
    </section>
  );
}

/** A `YYYY-MM` month in the person's language: "August 2026". */
function useMonthName() {
  const { language } = useLanguage();
  return (month: string) =>
    formatDate(new Date(`${month}-01T00:00:00Z`), language, { month: "long", year: "numeric", timeZone: "UTC" });
}

function Position({ page }: { page: GstPage }) {
  const w = useWords();
  const { language } = useLanguage();
  const monthName = useMonthName();
  const { position } = page;
  if (position.status !== "shown") return <p className="muted">{w.gstCouldNotRead(position.problems)}</p>;
  const period = position.period.includes("/") ? position.periodLabel : monthName(position.period);
  if (!position.recorded) return <p className="card">{w.gstNothingRecorded(period)}</p>;
  const { figures, totals } = position;
  const sum = (h: Heads) => h.igst + h.cgst + h.sgst;
  const rows: { label: string; heads: Heads; total: number }[] = [
    {
      label: w.gstOutputRow,
      heads: {
        igst: figures.outward.igst + figures.reverseCharge.igst,
        cgst: figures.outward.cgst + figures.reverseCharge.cgst,
        sgst: figures.outward.sgst + figures.reverseCharge.sgst,
      },
      total: totals.output,
    },
    { label: w.gstItcRow, heads: figures.itcNet, total: sum(figures.itcNet) },
    { label: w.gstPayRow, heads: figures.cash, total: totals.net },
  ];
  return (
    <>
      <div className="kpis">
        {[
          { label: w.gstOutput, value: totals.output },
          { label: w.gstItc, value: totals.itc },
          { label: w.gstNet, value: totals.net },
          ...(totals.carried > 0 ? [{ label: w.gstCarried, value: totals.carried }] : []),
        ].map((tile) => (
          <article className="kpi" key={tile.label}>
            <h3>{tile.label}</h3>
            <p className="kpi-now">
              <b>{rupees(tile.value)}</b>
            </p>
          </article>
        ))}
      </div>
      <div className="tbl-wrap">
        <table className="tbl">
          <caption className="sr-only">{w.gstPositionTitle(period)}</caption>
          <thead>
            <tr>
              <th scope="col">{w.gstTax}</th>
              <th scope="col" className="r">
                IGST
              </th>
              <th scope="col" className="r">
                CGST
              </th>
              <th scope="col" className="r">
                SGST
              </th>
              <th scope="col" className="r">
                {w.gstTotal}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <th scope="row">{row.label}</th>
                <td className="r">{rupees(row.heads.igst)}</td>
                <td className="r">{rupees(row.heads.cgst)}</td>
                <td className="r">{rupees(row.heads.sgst)}</td>
                <td className="r">{rupees(row.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {position.kept ? (
        <>
          <p className="small muted">
            {w.gstKeptVersion(position.kept.version, dayOf(position.kept.issuedAt.slice(0, 10), language))}
          </p>
          {position.kept.changed ? (
            <p className="card" role="status">
              {w.gstKeptChanged(position.kept.version, position.filing?.status === "done")}
            </p>
          ) : null}
        </>
      ) : (
        <p className="small muted">{position.filing ? w.gstNoVersionYet : w.gstNotLaidOut}</p>
      )}
      {position.notes.length > 0 ? (
        <ul className="small muted">
          {position.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

function Status({ status, daysLate, filedOn }: Pick<CalendarEntry, "status" | "daysLate" | "filedOn">) {
  const w = useWords();
  const { language } = useLanguage();
  if (status === "done") return <span className="tag tag-good">{w.gstStatusDone(dayOf(filedOn, language))}</span>;
  if (status === "before_lamp") return <span className="tag tag-good">{w.gstStatusBeforeLamp}</span>;
  if (status === "late") return <span className="tag tag-bad">{w.gstStatusLate(daysLate)}</span>;
  return <span className="tag tag-plain">{w.gstStatusDue}</span>;
}

function Export({ view, period }: { view: ExportView; period: string }) {
  const w = useWords();
  const { language } = useLanguage();
  const what = w.gstExportKind(view.kind);
  return (
    <article className="card">
      <header>
        <h4>{w.gstExportTitle(what, view.periodLabel)}</h4>
        <Status status={view.status} daysLate={view.daysLate} filedOn={view.filedOn} />
      </header>
      {!view.shown ? (
        <p className="muted">{w.gstExportHidden(view.problems ?? [])}</p>
      ) : (view.versions ?? []).length === 0 ? (
        <p className="muted">{w.gstNotPrepared(what, period)}</p>
      ) : (
        <ul>
          {(view.versions ?? []).map((version) => (
            <li key={version.version}>
              {w.gstVersion(version.version, dayOf(version.issuedAt.slice(0, 10), language))}{" "}
              {version.files.map((file) => (
                <a
                  key={file.part}
                  className="btn btn-small"
                  href={exportHref(view, version.version, file.part)}
                  download={file.name}
                >
                  {w.gstFilePart(file.part)}
                </a>
              ))}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function Calendar({ calendar }: { calendar: GstPage["calendar"] }) {
  const w = useWords();
  const { language } = useLanguage();
  const monthName = useMonthName();
  if (calendar.status !== "shown") return <p className="muted">{w.gstCouldNotRead(calendar.problems)}</p>;
  return (
    <>
      <p className="muted">{w.gstCalendarIntro}</p>
      {calendar.cycle === "off" ? <p className="small muted">{w.gstCycleOff}</p> : null}
      {calendar.filings.length === 0 ? (
        <p className="muted">{w.gstNothingDue(monthName(calendar.month))}</p>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <caption className="sr-only">{w.gstCalendarTitle(monthName(calendar.month))}</caption>
            <thead>
              <tr>
                <th scope="col">{w.gstFiling}</th>
                <th scope="col">{w.gstFor}</th>
                <th scope="col">{w.gstDueOn}</th>
                <th scope="col">{w.gstStatus}</th>
              </tr>
            </thead>
            <tbody>
              {calendar.filings.map((filing) => (
                <tr key={`${filing.obligation}|${filing.period}`}>
                  <th scope="row">{filing.name}</th>
                  <td>{filing.periodLabel}</td>
                  <td>{dayOf(filing.dueDate, language)}</td>
                  <td>
                    <Status status={filing.status} daysLate={filing.daysLate} filedOn={filing.filedOn} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {calendar.problems.length > 0 ? (
        <ul className="small muted">
          {calendar.problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

/** What the latest read for a month gave: the page, or why there is none. */
type Read = { readonly asked: string | undefined } & (
  { readonly page: GstPage } | { readonly problem: "refused" | "failed" }
);

/** A period's sections, as read for it. */
function PeriodSections({ page }: { page: GstPage }) {
  const w = useWords();
  const monthName = useMonthName();
  const period = monthName(page.month);
  return (
    <>
      <Section id="gst-position" title={w.gstPositionTitle(period)}>
        <Position page={page} />
      </Section>
      <Section id="gst-exports" title={w.gstExportsTitle}>
        <p className="muted">{w.gstExportsIntro}</p>
        {page.exports.length === 0 ? (
          <p className="muted">{w.gstNoExports(period)}</p>
        ) : (
          page.exports.map((view) => <Export key={view.filing} view={view} period={period} />)
        )}
      </Section>
      <Section id="gst-tds" title={w.gstTdsTitle}>
        {page.tds.length === 0 ? (
          <p className="muted">{w.gstNoTds}</p>
        ) : (
          page.tds.map((view) => <Export key={view.filing} view={view} period={view.periodLabel} />)
        )}
      </Section>
      <Section
        id="gst-calendar"
        title={w.gstCalendarTitle(page.calendar.status === "shown" ? monthName(page.calendar.month) : period)}
      >
        <Calendar calendar={page.calendar} />
      </Section>
    </>
  );
}

/** The page's heading and the period chosen, above what was read for it. */
function Period({
  month,
  onMonth,
  children,
}: {
  month: string;
  onMonth: (month: string) => void;
  children: ReactNode;
}) {
  const w = useWords();
  return (
    <>
      <div className="hello">
        <h2>{w.gstHeading}</h2>
        <p className="muted">{w.gstIntro}</p>
      </div>
      <div className="toolbar">
        <label className="field-inline">
          {w.gstPeriod}{" "}
          <input
            type="month"
            value={month}
            onChange={(e) => {
              if (MONTH.test(e.target.value)) onMonth(e.target.value);
            }}
          />
        </label>
      </div>
      {children}
    </>
  );
}

export function Gst({ api }: { api: GstApi }) {
  const w = useWords();
  const monthName = useMonthName();
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [read, setRead] = useState<Read | undefined>(undefined);

  useEffect(() => {
    let current = true;
    api.gst(month).then(
      (page) => {
        if (current) setRead({ asked: month, page });
      },
      (error: unknown) => {
        if (current)
          setRead({ asked: month, problem: error instanceof ApiError && error.status === 403 ? "refused" : "failed" });
      },
    );
    return () => {
      current = false;
    };
  }, [api, month]);

  // Only the read for the month chosen is shown: another month's never stays under its name, nor a failure after.
  const now = read?.asked === month ? read : undefined;
  if (month === undefined)
    return !now ? (
      <p role="status">{w.gstLoading}</p>
    ) : "problem" in now ? (
      <p role="alert">{now.problem === "refused" ? w.gstRefused : w.gstLoadFailed}</p>
    ) : (
      <Period month={now.page.month} onMonth={setMonth}>
        <PeriodSections page={now.page} />
      </Period>
    );
  return (
    <Period month={month} onMonth={setMonth}>
      {!now ? (
        <p role="status">{w.gstPeriodLoading(monthName(month))}</p>
      ) : "problem" in now ? (
        <p role="alert">{now.problem === "refused" ? w.gstRefused : w.gstPeriodFailed(monthName(month))}</p>
      ) : (
        <PeriodSections page={now.page} />
      )}
    </Period>
  );
}
